import { FormControlLabel, FormHelperText, Switch } from "@mui/material";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import {
  useHubRideActionConfirm,
  usePageContext,
  useRidesBulkActions,
} from "@ryde/redux";
import {
  bulkRebuildRideDrawerAtom,
  HubRideActionKind,
  selectedRidesSelector,
  selectedTableRowsIdsAtomFamily,
} from "@ryde/shared";

import { BulkRebuildSubOptionStyled } from "./parts";

export const BulkRebuildRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity } = usePageContext();

  const selectedRideIds = useRecoilValue(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const selectedRides = useRecoilValue(selectedRidesSelector);

  const setBulkRebuildRideDrawerOpened = useSetRecoilState(
    bulkRebuildRideDrawerAtom,
  );

  const [isIncludePastRides, setIncludePastRides] = useState(false);
  const [isIncludeSentRides, setIncludeSentRides] = useState(false);
  const [isRecalculateCoordinates, setRecalculateCoordinates] = useState(false);
  const { bulkRideRebuild } = useRidesBulkActions();
  const { confirmSelectedHubRidesAction } = useHubRideActionConfirm();

  const onCloseDrawer = useEvent((): void => {
    setBulkRebuildRideDrawerOpened(false);
  });

  const onIncludePastRidesChange = useEvent((isChecked: boolean): void => {
    setIncludePastRides(isChecked);
    // Hidden together with this switch — it must not stay on behind it.
    if (!isChecked) {
      setRecalculateCoordinates(false);
    }
  });

  const onNextClick = useEvent(async (): Promise<void> => {
    const isHubModifyConfirmed = await confirmSelectedHubRidesAction({
      selectedRideIds,
      rides: selectedRides,
      actionKind: HubRideActionKind.Modify,
    });

    if (!isHubModifyConfirmed) {
      return;
    }

    const result = await bulkRideRebuild({
      ids: selectedRideIds,
      isIncludePastRides,
      isIncludeSentRides,
      isRecalculateCoordinates,
    });

    if (result?.success) {
      onCloseDrawer();
    }
  });

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer}>
      <FormDrawer
        onCloseDrawer={onCloseDrawer}
        onCancelClick={onCloseDrawer}
        title={t("general.REBUILD_RIDE")}
        onSaveClick={onNextClick}
        footerSaveLabel={t("general.NEXT")}
      >
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={isIncludePastRides}
                onChange={(_, checked) => onIncludePastRidesChange(checked)}
              />
            }
            label={t("general.REBUILD_ALSO_PAST_RIDE")}
          />

          <FormHelperText>
            {t("general.REBUILD_ALSO_PAST_RIDE_HELPER")}
          </FormHelperText>

          {isIncludePastRides && (
            <BulkRebuildSubOptionStyled>
              <FormControlLabel
                control={
                  <Switch
                    checked={isRecalculateCoordinates}
                    onChange={(_, checked) =>
                      setRecalculateCoordinates(checked)
                    }
                  />
                }
                label={t("general.RECALCULATE_ALSO_COORDINATES")}
              />
              <FormHelperText>
                {t("general.RECALCULATE_ALSO_COORDINATES_HELPER")}
              </FormHelperText>
            </BulkRebuildSubOptionStyled>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isIncludeSentRides}
                onChange={(_, checked) => setIncludeSentRides(checked)}
              />
            }
            label={t("general.REBUILD_ALSO_SENT_RIDE")}
          />
          <FormHelperText>
            {t("general.REBUILD_ALSO_SENT_RIDE_HELPER")}
          </FormHelperText>
        </div>
      </FormDrawer>
    </BaseDrawer>
  );
};
